import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut, updatePassword } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, getDoc, updateDoc, addDoc, setDoc, deleteDoc, collection, getDocs, orderBy, query, where, onSnapshot, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '../components/firebase-config.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome, faUserMd, faUser, faCalendarAlt, faKey, faBars, faSignOutAlt,
  faEdit, faEye, faFilter, faHistory, faTrash, faArrowLeft, faChevronLeft, 
  faChevronRight, faClock, faPhone, faEnvelope, faClinicMedical, faCheckCircle,
  faExclamationCircle, faList, faSearch, faLink, faUnlink, faArchive, faTimes,
  faMapMarkerAlt, faRedo, faStop, faFile, faDownload, faIdCard, faCog, faInfoCircle,
  faEllipsisV, faComments, faComment, faPaperPlane, faCircleNotch, faPaperclip,
  faMicrophone, faFileImage, faCalendarDay, faCalendarWeek, faLock, faCheck,
  faCloudUploadAlt, faCloudDownloadAlt, faPlus, faSave, faFilePdf, faFileExport,
  faDatabase, faShieldAlt, faUserCog, faUserPlus, faUserMinus, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import "./Medecins.css"

const Medecins = () => {
  // États essentiels
  const auth = getAuth();
  const navigate = useNavigate();
  const storage = getStorage();
  
  const [medecinData, setMedecinData] = useState(null);
  const [activeTab, setActiveTab] = useState('rendezvous');
  const [activeSubTab, setActiveSubTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  
  // États pour les données
  const [rendezvous, setRendezvous] = useState([]);
  const [patients, setPatients] = useState([]);
  const [structures, setStructures] = useState([]);
  const [affiliatedStructures, setAffiliatedStructures] = useState([]);
  const [pendingAffiliations, setPendingAffiliations] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [backups, setBackups] = useState([]);
  const [calendarView, setCalendarView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // États pour les filtres
  const [rdvFilters, setRdvFilters] = useState({
    status: 'all',
    dateRange: 'all',
    structure: 'all'
  });
  const [patientFilters, setPatientFilters] = useState({
    sortBy: 'name',
    onlyActive: false
  });
  
  // États pour les modales et formulaires
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [showRdvModal, setShowRdvModal] = useState(false);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [backupName, setBackupName] = useState('');
  const [backupDescription, setBackupDescription] = useState('');
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);
  const [newRdv, setNewRdv] = useState({
    patientId: '',
    date: new Date().toISOString().split('T')[0],
    heure: '09:00',
    duree: 30,
    motif: '',
    notes: '',
    statut: 'confirmé',
    structureId: ''
  });
  const [newPatient, setNewPatient] = useState({
    nom: '',
    prenom: '',
    dateNaissance: '',
    telephone: '',
    email: '',
    adresse: '',
    diagnostics: '',
    notes: '',
    statut: 'actif'
  });
  
  // États UI mobile
  const [showDetailView, setShowDetailView] = useState(false);
  const [detailType, setDetailType] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  
  // Vérification de l'authentification
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          setLoading(true);
          const docRef = doc(db, 'medecins', user.uid);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            setMedecinData(docSnap.data());
            setupListeners(user.uid);
          } else {
            navigate('/auth');
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des données:", error);
        } finally {
          setLoading(false);
        }
      } else {
        navigate('/auth');
      }
    });
    
    return () => unsubscribeAuth();
  }, [auth, navigate]);
  
  // Configuration des écouteurs
  const setupListeners = (medecinId) => {
    // Écouteur pour les rendez-vous
    const rdvUnsubscribe = onSnapshot(
      query(collection(db, 'rendezvous'), where("medecinId", "==", medecinId)),
      (snapshot) => {
        const rdvData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRendezvous(rdvData);
      }
    );
    
    // Écouteur pour les structures affiliées
    const affiliationsUnsubscribe = onSnapshot(
      query(
        collection(db, 'affiliationRequests'),
        where("medecinId", "==", medecinId),
        where("status", "in", ["accepted"])
      ),
      async (snapshot) => {
        const structureIds = snapshot.docs.map(doc => doc.data().structureId);
        
        if (structureIds.length === 0) {
          setAffiliatedStructures([]);
          return;
        }
        
        const structures = [];
        for (const structureId of structureIds) {
          const structureDoc = await getDoc(doc(db, 'structures', structureId));
          if (structureDoc.exists()) {
            structures.push({
              id: structureId,
              ...structureDoc.data()
            });
          }
        }
        
        setAffiliatedStructures(structures);
      }
    );
    
    // Écouteur pour les demandes d'affiliation en attente
    const pendingAffiliationsUnsubscribe = onSnapshot(
      query(
        collection(db, 'affiliationRequests'),
        where("medecinId", "==", medecinId),
        where("status", "==", "pending")
      ),
      async (snapshot) => {
        const pendingRequests = [];
        
        for (const docSnapshot of snapshot.docs) {
          const requestData = docSnapshot.data();
          let structureName = "Structure inconnue";
          
          try {
            const structureDoc = await getDoc(doc(db, 'structures', requestData.structureId));
            if (structureDoc.exists()) {
              structureName = structureDoc.data().nom;
            }
          } catch (error) {
            console.error("Erreur lors de la récupération de la structure:", error);
          }
          
          pendingRequests.push({
            id: docSnapshot.id,
            ...requestData,
            structureName
          });
        }
        
        setPendingAffiliations(pendingRequests);
      }
    );
    
    // Écouteur pour les messages non lus
    const messagesUnsubscribe = onSnapshot(
      query(
        collection(db, 'conversations'),
        where("unreadBy", "array-contains", medecinId)
      ),
      (snapshot) => {
        setUnreadMessagesCount(snapshot.docs.length);
      }
    );
    
    // Écouteur pour les sauvegardes
    const backupsUnsubscribe = onSnapshot(
      query(
        collection(db, 'backups'),
        where("medecinId", "==", medecinId),
        orderBy("createdAt", "desc")
      ),
      (snapshot) => {
        const backupsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBackups(backupsData);
      }
    );
    
    // Écouteur pour les structures disponibles
    const structuresUnsubscribe = onSnapshot(
      collection(db, 'structures'),
      (snapshot) => {
        const structuresData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStructures(structuresData);
      }
    );
    
    // Retourner les fonctions de nettoyage
    return () => {
      rdvUnsubscribe();
      affiliationsUnsubscribe();
      pendingAffiliationsUnsubscribe();
      messagesUnsubscribe();
      backupsUnsubscribe();
      structuresUnsubscribe();
    };
  };
  
  // Effet pour charger les patients basés sur les rendez-vous
  useEffect(() => {
    if (rendezvous.length > 0) {
      const uniquePatientIds = [...new Set(rendezvous.map(rdv => rdv.patientId).filter(Boolean))];
      
      if (uniquePatientIds.length === 0) {
        setPatients([]);
        return;
      }
      
      const fetchPatients = async () => {
        const patientsData = [];
        
        for (const patientId of uniquePatientIds) {
          try {
            const patientDoc = await getDoc(doc(db, 'patients', patientId));
            if (patientDoc.exists()) {
              patientsData.push({
                id: patientId,
                ...patientDoc.data()
              });
            }
          } catch (error) {
            console.error(`Erreur lors de la récupération du patient ${patientId}:`, error);
          }
        }
        
        setPatients(patientsData);
      };
      
      fetchPatients();
    } else {
      setPatients([]);
    }
  }, [rendezvous]);
  
  // Charger les conversations
  const loadConversations = async () => {
    if (!auth.currentUser) return;
    
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', auth.currentUser.uid)
    );
    
    const conversationsSnapshot = await getDocs(conversationsQuery);
    const conversationsList = [];
    
    for (const docSnapshot of conversationsSnapshot.docs) {
      const conversationData = docSnapshot.data();
      const otherParticipantId = conversationData.participants.find(
        id => id !== auth.currentUser.uid
      );
      
      if (otherParticipantId) {
        let patient = { id: otherParticipantId, nom: "Patient inconnu", prenom: "" };
        
        try {
          const patientDoc = await getDoc(doc(db, 'patients', otherParticipantId));
          if (patientDoc.exists()) {
            patient = {
              id: otherParticipantId,
              ...patientDoc.data()
            };
          }
        } catch (error) {
          console.error("Erreur lors de la récupération du patient:", error);
        }
        
        conversationsList.push({
          id: docSnapshot.id,
          ...conversationData,
          patient: patient,
          unreadCount: conversationData.unreadBy && 
            conversationData.unreadBy.includes(auth.currentUser.uid) ? 1 : 0
        });
      }
    }
    
    setConversations(conversationsList);
  };
  
  // Charger les conversations quand l'onglet messagerie est actif
  useEffect(() => {
    if (activeTab === 'messagerie') {
      loadConversations();
    }
  }, [activeTab]);
  
  // Charger les messages d'une conversation
  const fetchMessages = async (conversationId) => {
    if (!conversationId) return () => {};
    
    try {
      const messagesQuery = query(
        collection(db, 'conversations', conversationId, 'messages'),
        orderBy('timestamp', 'asc')
      );
      
      const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
        const messagesList = snapshot.docs.map(docSnapshot => ({
          id: docSnapshot.id,
          ...docSnapshot.data()
        }));
        
        setMessages(messagesList);
        
        // Marquer les messages comme lus
        markConversationAsRead(conversationId);
        
        // Scroll to bottom
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      });
      
      return unsubscribe;
    } catch (err) {
      console.error("Erreur lors du chargement des messages:", err);
      return () => {};
    }
  };
  
  // Marquer une conversation comme lue
  const markConversationAsRead = async (conversationId) => {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnapshot = await getDoc(conversationRef);
      
      if (conversationSnapshot.exists()) {
        const data = conversationSnapshot.data();
        if (data.unreadBy && data.unreadBy.includes(auth.currentUser.uid)) {
          await updateDoc(conversationRef, {
            unreadBy: data.unreadBy.filter(id => id !== auth.currentUser.uid)
          });
          
          setConversations(prevConversations => 
            prevConversations.map(conv => 
              conv.id === conversationId ? {...conv, unreadCount: 0} : conv
            )
          );
        }
      }
    } catch (err) {
      console.error("Erreur lors du marquage de la conversation comme lue:", err);
    }
  };
  
  // Fonction pour envoyer un message
  const sendMessage = async (e) => {
    e.preventDefault();
    
    if ((!newMessage.trim() && !attachmentFile && !audioBlob) || !selectedConversation) {
      return;
    }
    
    try {
      const messageData = {
        text: newMessage.trim(),
        senderId: auth.currentUser.uid,
        senderName: `Dr. ${medecinData.prenom} ${medecinData.nom}`,
        timestamp: serverTimestamp(),
        read: false
      };
      
      // Si un fichier est attaché, l'uploader
      if (attachmentFile) {
        const formData = new FormData();
        formData.append('file', attachmentFile);
        formData.append('upload_preset', 'fichiers');
        formData.append('folder', `conversations/${selectedConversation.id}`);
        
        const response = await fetch(
          `https://api.cloudinary.com/v1_1/dodsr1s0d/upload`,
          {
            method: 'POST',
            body: formData,
          }
        );
        
        const data = await response.json();
        
        if (response.ok) {
          messageData.attachment = {
            url: data.secure_url,
            name: attachmentFile.name,
            type: attachmentFile.type,
            size: attachmentFile.size,
            publicId: data.public_id
          };
        }
      }
      
      // Si un enregistrement audio est présent, l'uploader
      if (audioBlob) {
        const fileName = `audio_${Date.now()}.webm`;
        const audioFile = new File([audioBlob], fileName, { type: 'audio/webm' });
        
        const formData = new FormData();
        formData.append('file', audioFile);
        formData.append('upload_preset', 'fichiers');
        formData.append('folder', `conversations/${selectedConversation.id}`);
        formData.append('resource_type', 'video');
        
        const response = await fetch(
          `https://api.cloudinary.com/v1_1/dodsr1s0d/upload`,
          {
            method: 'POST',
            body: formData,
          }
        );
        
        const data = await response.json();
        
        if (response.ok) {
          messageData.audioAttachment = {
            url: data.secure_url,
            name: fileName,
            type: 'audio/webm',
            duration: recordingTime,
            publicId: data.public_id
          };
        }
      }
      
      // Ajouter le message à la collection
      await addDoc(collection(db, 'conversations', selectedConversation.id, 'messages'), messageData);
      
      // Mettre à jour la conversation
      await updateDoc(doc(db, 'conversations', selectedConversation.id), {
        lastMessage: messageData.text || (attachmentFile ? "Pièce jointe" : "Note vocale"),
        lastMessageAt: serverTimestamp(),
        unreadBy: [selectedConversation.patient.id]
      });
      
      // Réinitialiser les champs
      setNewMessage('');
      setAttachmentFile(null);
      setAudioBlob(null);
      setRecordingTime(0);
      
    } catch (err) {
      console.error("Erreur lors de l'envoi du message:", err);
    }
  };
  
  // Fonctions pour l'enregistrement audio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      
      const timer = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      setRecordingTimer(timer);
      
    } catch (error) {
      console.error("Erreur lors de l'accès au microphone:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
      
      if (recordingTimer) {
        clearInterval(recordingTimer);
        setRecordingTimer(null);
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    
    setIsRecording(false);
    setAudioBlob(null);
    setRecordingTime(0);
    
    if (recordingTimer) {
      clearInterval(recordingTimer);
      setRecordingTimer(null);
    }
  };
  
  // Fonction pour formater le temps d'enregistrement
  const formatRecordingTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };
  
  // Fonction pour calculer l'âge
  const calculateAge = (dateNaissance) => {
    if (!dateNaissance) return 'N/A';
    
    const birthDate = new Date(dateNaissance);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };
  
  // Fonction pour gérer la déconnexion
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/auth');
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    }
  };
  
  // Fonction pour afficher les détails
  const showDetails = (type, item) => {
    setDetailType(type);
    setDetailItem(item);
    setShowDetailView(true);
  };
  
  // Fonction pour changer le mot de passe
  const handlePasswordChange = async () => {
    if (newPasswordValue !== confirmPassword) {
      setPasswordError("Les mots de passe ne correspondent pas");
      return;
    }
    
    try {
      setLoading(true);
      await updatePassword(auth.currentUser, newPasswordValue);
      setMessage("Mot de passe mis à jour avec succès");
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPasswordValue('');
      setConfirmPassword('');
      setPasswordError('');
    } catch (error) {
      setPasswordError(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour créer une sauvegarde
  const createBackup = async () => {
    if (!backupName.trim()) {
      setMessage("Veuillez donner un nom à votre sauvegarde");
      return;
    }
    
    try {
      setLoading(true);
      
      // Récupérer les données des patients sélectionnés
      const patientsData = [];
      for (const patientId of selectedPatientIds) {
        const patientDoc = await getDoc(doc(db, 'patients', patientId));
        if (patientDoc.exists()) {
          patientsData.push({
            id: patientId,
            ...patientDoc.data()
          });
        }
      }
      
      // Créer la sauvegarde
      await addDoc(collection(db, 'backups'), {
        name: backupName,
        description: backupDescription,
        medecinId: auth.currentUser.uid,
        patientsCount: patientsData.length,
        patientsData: patientsData,
        createdAt: serverTimestamp()
      });
      
      setMessage("Sauvegarde créée avec succès");
      setShowBackupModal(false);
      setBackupName('');
      setBackupDescription('');
      setSelectedPatientIds([]);
    } catch (error) {
      console.error("Erreur lors de la création de la sauvegarde:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour restaurer une sauvegarde
  const restoreBackup = async (backupId) => {
    try {
      setLoading(true);
      
      const backupDoc = await getDoc(doc(db, 'backups', backupId));
      if (!backupDoc.exists()) {
        setMessage("Sauvegarde introuvable");
        return;
      }
      
      const backupData = backupDoc.data();
      
      // Restaurer les patients
      for (const patient of backupData.patientsData) {
        await setDoc(doc(db, 'patients', patient.id), {
          ...patient,
          lastUpdated: serverTimestamp()
        });
      }
      
      setMessage(`Sauvegarde "${backupData.name}" restaurée avec succès`);
    } catch (error) {
      console.error("Erreur lors de la restauration de la sauvegarde:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour supprimer une sauvegarde
  const deleteBackup = async (backupId) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'backups', backupId));
      setMessage("Sauvegarde supprimée avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression de la sauvegarde:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour créer un rendez-vous
  const createRendezVous = async () => {
    try {
      setLoading(true);
      
      // Récupérer les informations du patient
      let patientNom = "";
      let patientPrenom = "";
      
      if (newRdv.patientId) {
        const patientDoc = await getDoc(doc(db, 'patients', newRdv.patientId));
        if (patientDoc.exists()) {
          const patientData = patientDoc.data();
          patientNom = patientData.nom;
          patientPrenom = patientData.prenom;
        }
      }
      
      // Créer le rendez-vous
      await addDoc(collection(db, 'rendezvous'), {
        medecinId: auth.currentUser.uid,
        patientId: newRdv.patientId,
        patientNom: `${patientPrenom} ${patientNom}`,
        date: newRdv.date,
        heure: newRdv.heure,
        duree: newRdv.duree,
        motif: newRdv.motif,
        notes: newRdv.notes,
        statut: newRdv.statut,
        structureId: newRdv.structureId,
        createdAt: serverTimestamp()
      });
      
      setMessage("Rendez-vous créé avec succès");
      setShowRdvModal(false);
      setNewRdv({
        patientId: '',
        date: new Date().toISOString().split('T')[0],
        heure: '09:00',
        duree: 30,
        motif: '',
        notes: '',
        statut: 'confirmé',
        structureId: ''
      });
    } catch (error) {
      console.error("Erreur lors de la création du rendez-vous:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour mettre à jour un rendez-vous
  const updateRendezVous = async (rdvId, updatedData) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, 'rendezvous', rdvId), {
        ...updatedData,
        updatedAt: serverTimestamp()
      });
      setMessage("Rendez-vous mis à jour avec succès");
      setShowDetailView(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rendez-vous:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour supprimer un rendez-vous
  const deleteRendezVous = async (rdvId) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'rendezvous', rdvId));
      setMessage("Rendez-vous supprimé avec succès");
      setShowDetailView(false);
    } catch (error) {
      console.error("Erreur lors de la suppression du rendez-vous:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour créer un patient
  const createPatient = async () => {
    try {
      setLoading(true);
      
      const patientRef = await addDoc(collection(db, 'patients'), {
        ...newPatient,
        mede: auth.currentUser.uid,
        createdAt: serverTimestamp()
      });
      
      setMessage("Patient créé avec succès");
      setShowPatientModal(false);
      setNewPatient({
        nom: '',
        prenom: '',
        dateNaissance: '',
        telephone: '',
        email: '',
        adresse: '',
        diagnostics: '',
        notes: '',
        statut: 'actif'
      });
      
      // Créer automatiquement une conversation avec ce patient
      await addDoc(collection(db, 'conversations'), {
        participants: [auth.currentUser.uid, patientRef.id],
        lastMessage: "Nouvelle conversation",
        lastMessageAt: serverTimestamp(),
        unreadBy: []
      });
      
    } catch (error) {
      console.error("Erreur lors de la création du patient:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour mettre à jour un patient
  const updatePatient = async (patientId, updatedData) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, 'patients', patientId), {
        ...updatedData,
        updatedAt: serverTimestamp()
      });
      setMessage("Patient mis à jour avec succès");
      setShowDetailView(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du patient:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour supprimer un patient
  const deletePatient = async (patientId) => {
    try {
      setLoading(true);
      
      // Supprimer les rendez-vous associés
      const rdvQuery = query(collection(db, 'rendezvous'), where('patientId', '==', patientId));
      const rdvSnapshot = await getDocs(rdvQuery);
      
      const batch = writeBatch(db);
      rdvSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Supprimer les conversations associées
      const convQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', patientId)
      );
      const convSnapshot = await getDocs(convQuery);
      
      convSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Supprimer le patient
      batch.delete(doc(db, 'patients', patientId));
      await batch.commit();
      
      setMessage("Patient supprimé avec succès");
      setShowDetailView(false);
    } catch (error) {
      console.error("Erreur lors de la suppression du patient:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour demander une affiliation
  const requestAffiliation = async (structureId) => {
    try {
      setLoading(true);
      
      // Vérifier si une demande existe déjà
      const existingQuery = query(
        collection(db, 'affiliationRequests'),
        where('medecinId', '==', auth.currentUser.uid),
        where('structureId', '==', structureId)
      );
      const existingSnapshot = await getDocs(existingQuery);
      
      if (!existingSnapshot.empty) {
        setMessage("Une demande d'affiliation existe déjà pour cette structure");
        return;
      }
      
      // Créer la demande
      await addDoc(collection(db, 'affiliationRequests'), {
        medecinId: auth.currentUser.uid,
        structureId: structureId,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      
      setMessage("Demande d'affiliation envoyée avec succès");
    } catch (error) {
      console.error("Erreur lors de la demande d'affiliation:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour annuler une demande d'affiliation
  const cancelAffiliationRequest = async (requestId) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'affiliationRequests', requestId));
      setMessage("Demande d'affiliation annulée");
    } catch (error) {
      console.error("Erreur lors de l'annulation de la demande:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour se désaffilier d'une structure
  const removeAffiliation = async (structureId) => {
    try {
      setLoading(true);
      
      // Trouver la demande d'affiliation
      const affiliationQuery = query(
        collection(db, 'affiliationRequests'),
        where('medecinId', '==', auth.currentUser.uid),
        where('structureId', '==', structureId),
        where('status', '==', 'accepted')
      );
      const affiliationSnapshot = await getDocs(affiliationQuery);
      
      if (affiliationSnapshot.empty) {
        setMessage("Affiliation introuvable");
        return;
      }
      
      // Supprimer l'affiliation
      await deleteDoc(doc(db, 'affiliationRequests', affiliationSnapshot.docs[0].id));
      
      setMessage("Désaffiliation réussie");
    } catch (error) {
      console.error("Erreur lors de la désaffiliation:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour mettre à jour les disponibilités
  const updateAvailability = async (day, isActive, startTime, endTime) => {
    try {
      setLoading(true);
      
      const medecinRef = doc(db, 'medecins', auth.currentUser.uid);
      const disponibilites = medecinData.disponibilites || {};
      
      disponibilites[day] = {
        actif: isActive,
        debut: startTime,
        fin: endTime
      };
      
      await updateDoc(medecinRef, { disponibilites });
      
      setMedecinData({
        ...medecinData,
        disponibilites
      });
      
      setMessage("Disponibilités mises à jour");
    } catch (error) {
      console.error("Erreur lors de la mise à jour des disponibilités:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour mettre à jour le profil
  const updateProfile = async (updatedData) => {
    try {
      setLoading(true);
      
      await updateDoc(doc(db, 'medecins', auth.currentUser.uid), {
        ...updatedData,
        updatedAt: serverTimestamp()
      });
      
      setMedecinData({
        ...medecinData,
        ...updatedData
      });
      
      setMessage("Profil mis à jour avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil:", error);
      setMessage(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonctions pour le calendrier
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };
  
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    
    // Jours du mois précédent
    const prevMonthDays = getDaysInMonth(year, month - 1);
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        month: month - 1,
        year: month === 0 ? year - 1 : year,
        isCurrentMonth: false
      });
    }
    
    // Jours du mois courant
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        month,
        year,
        isCurrentMonth: true
      });
    }
    
    // Jours du mois suivant
    const remainingDays = 42 - days.length; // 6 semaines * 7 jours
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        month: month + 1,
        year: month === 11 ? year + 1 : year,
        isCurrentMonth: false
      });
    }
    
    return days;
  };
  
  const getRendezVousForDate = (year, month, day) => {
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return rendezvous.filter(rdv => rdv.date === dateString);
  };
  
  const getWeekDays = () => {
    const days = [];
    const date = new Date(currentDate);
    const day = date.getDay();
    
    // Reculer au début de la semaine (dimanche)
    date.setDate(date.getDate() - day);
    
    // Générer les 7 jours de la semaine
    for (let i = 0; i < 7; i++) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    
    return days;
  };
  
  // Filtrer les rendez-vous
  const filteredRendezVous = useMemo(() => {
    let filtered = [...rendezvous];
    
    // Filtrer par statut
    if (rdvFilters.status !== 'all') {
      filtered = filtered.filter(rdv => rdv.statut === rdvFilters.status);
    }
    
    // Filtrer par date
    if (rdvFilters.dateRange !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (rdvFilters.dateRange === 'today') {
        const todayString = today.toISOString().split('T')[0];
        filtered = filtered.filter(rdv => rdv.date === todayString);
      } else if (rdvFilters.dateRange === 'week') {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        filtered = filtered.filter(rdv => {
          const rdvDate = new Date(rdv.date);
          return rdvDate >= weekStart && rdvDate <= weekEnd;
        });
      } else if (rdvFilters.dateRange === 'month') {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        
        filtered = filtered.filter(rdv => {
          const rdvDate = new Date(rdv.date);
          return rdvDate >= monthStart && rdvDate <= monthEnd;
        });
      }
    }
    
    // Filtrer par structure
    if (rdvFilters.structure !== 'all') {
      filtered = filtered.filter(rdv => rdv.structureId === rdvFilters.structure);
    }
    
    // Filtrer par recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(rdv => 
        rdv.patientNom?.toLowerCase().includes(term) || 
        rdv.motif?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  }, [rendezvous, rdvFilters, searchTerm]);
  
  // Filtrer les patients
  const filteredPatients = useMemo(() => {
    let filtered = [...patients];
    
    // Filtrer par statut
    if (patientFilters.onlyActive) {
      filtered = filtered.filter(patient => patient.statut === 'actif');
    }
    
    // Filtrer par recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(patient => 
        patient.nom?.toLowerCase().includes(term) || 
        patient.prenom?.toLowerCase().includes(term) ||
        patient.email?.toLowerCase().includes(term)
      );
    }
    
    // Trier
    if (patientFilters.sortBy === 'name') {
      filtered.sort((a, b) => {
        const nameA = `${a.nom} ${a.prenom}`.toLowerCase();
        const nameB = `${b.nom} ${b.prenom}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else if (patientFilters.sortBy === 'date') {
      filtered.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt.toDate()) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt.toDate()) : new Date(0);
        return dateB - dateA;
      });
    }
    
    return filtered;
  }, [patients, patientFilters, searchTerm]);
  
  // Filtrer les conversations
  const filteredConversations = useMemo(() => {
    if (!searchTerm) return conversations;
    
    const term = searchTerm.toLowerCase();
    return conversations.filter(conv => 
      conv.patient.nom?.toLowerCase().includes(term) || 
      conv.patient.prenom?.toLowerCase().includes(term) ||
      conv.lastMessage?.toLowerCase().includes(term)
    );
  }, [conversations, searchTerm]);
  
  // Filtrer les structures
  const filteredStructures = useMemo(() => {
    if (!searchTerm) return structures;
    
    const term = searchTerm.toLowerCase();
    return structures.filter(structure => 
      structure.nom?.toLowerCase().includes(term) || 
      structure.adresse?.toLowerCase().includes(term)
    );
  }, [structures, searchTerm]);
  
  // Filtrer les sauvegardes
  const filteredBackups = useMemo(() => {
    if (!searchTerm) return backups;
    
    const term = searchTerm.toLowerCase();
    return backups.filter(backup => 
      backup.name?.toLowerCase().includes(term) || 
      backup.description?.toLowerCase().includes(term)
    );
  }, [backups, searchTerm]);
  
  // Afficher l'écran de chargement
  if (loading) {
    return (
      <div className="mobile-loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }
  
  return (
    <div className="mobile-dashboard">
      {/* Menu latéral */}
      {showMenu && (
        <div className="mobile-side-menu">
          <div className="side-menu-header">
            <div className="side-menu-user">
              <div className="side-menu-avatar">
                {medecinData?.prenom?.charAt(0)}{medecinData?.nom?.charAt(0)}
              </div>
              <div className="side-menu-info">
                <div className="side-menu-name">Dr. {medecinData?.prenom} {medecinData?.nom}</div>
                <div className="side-menu-specialty">{medecinData?.specialite}</div>
              </div>
            </div>
            <button className="side-menu-close" onClick={() => setShowMenu(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="side-menu-content">
            <div className="side-menu-section">
              <h4>Compte</h4>
              <button className="side-menu-item" onClick={() => {
                setShowMenu(false);
                setActiveTab('profil');
              }}>
                <FontAwesomeIcon icon={faUserMd} />
                <span>Mon profil</span>
              </button>
              <button className="side-menu-item" onClick={() => {
                setShowMenu(false);
                setShowPasswordModal(true);
              }}>
                <FontAwesomeIcon icon={faKey} />
                <span>Changer le mot de passe</span>
              </button>
            </div>
            
            <div className="side-menu-section">
              <h4>Données</h4>
              <button className="side-menu-item" onClick={() => {
                setShowMenu(false);
                setActiveTab('sauvegardes');
              }}>
                <FontAwesomeIcon icon={faDatabase} />
                <span>Sauvegardes</span>
              </button>
            </div>
            
            <div className="side-menu-section">
              <h4>Structures</h4>
              <button className="side-menu-item" onClick={() => {
                setShowMenu(false);
                setActiveTab('affiliations');
                setActiveSubTab('affiliated');
              }}>
                <FontAwesomeIcon icon={faLink} />
                <span>Mes affiliations</span>
              </button>
              <button className="side-menu-item" onClick={() => {
                setShowMenu(false);
                setActiveTab('affiliations');
                setActiveSubTab('available');
              }}>
                <FontAwesomeIcon icon={faSearch} />
                <span>Rechercher des structures</span>
              </button>
            </div>
            
            <div className="side-menu-section">
              <h4>Paramètres</h4>
              <button className="side-menu-item" onClick={() => {
                setShowMenu(false);
                setActiveTab('settings');
              }}>
                <FontAwesomeIcon icon={faCog} />
                <span>Paramètres</span>
              </button>
            </div>
            
            <button className="side-menu-logout" onClick={handleLogout}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Overlay du menu latéral */}
      {showMenu && (
        <div className="side-menu-overlay" onClick={() => setShowMenu(false)}></div>
      )}
      
      {/* Header */}
      {!showDetailView && (
        <header className="mobile-header">
          <button className="mobile-menu-btn" onClick={() => setShowMenu(true)}>
            <FontAwesomeIcon icon={faBars} />
          </button>
          <div className="mobile-header-title">
            {activeTab === 'rendezvous' && 'Rendez-vous'}
            {activeTab === 'patients' && 'Patients'}
            {activeTab === 'messagerie' && 'Messagerie'}
            {activeTab === 'affiliations' && 'Structures'}
            {activeTab === 'profil' && 'Mon Profil'}
            {activeTab === 'sauvegardes' && 'Sauvegardes'}
            {activeTab === 'settings' && 'Paramètres'}
          </div>
          <div className="mobile-header-actions">
            {activeTab === 'rendezvous' && (
              <button className="header-action-btn" onClick={() => setShowRdvModal(true)}>
                <FontAwesomeIcon icon={faPlus} />
              </button>
            )}
            {activeTab === 'patients' && (
              <button className="header-action-btn" onClick={() => setShowPatientModal(true)}>
                <FontAwesomeIcon icon={faPlus} />
              </button>
            )}
            {activeTab === 'affiliations' && activeSubTab === 'available' && (
              <button className="header-action-btn" onClick={() => setShowFilters(!showFilters)}>
                <FontAwesomeIcon icon={faFilter} />
              </button>
            )}
            {activeTab === 'sauvegardes' && (
              <button className="header-action-btn" onClick={() => setShowBackupModal(true)}>
                <FontAwesomeIcon icon={faPlus} />
              </button>
            )}
          </div>
        </header>
      )}
      
      {/* Alerte de message */}
      {message && (
        <div className="mobile-alert">
          <span>{message}</span>
          <button className="btn-close" onClick={() => setMessage('')}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      
      {/* Barre de recherche */}
      {!showDetailView && activeTab !== 'profil' && activeTab !== 'settings' && (
        <div className="mobile-search">
          <div className="search-input">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input 
              type="text" 
              placeholder="Rechercher..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      )}
      
      {/* Filtres */}
      {!showDetailView && activeTab === 'rendezvous' && showFilters && (
        <div className="mobile-filters">
          <div className="filter-group">
            <label>Statut</label>
            <select 
              value={rdvFilters.status}
              onChange={(e) => setRdvFilters({...rdvFilters, status: e.target.value})}
            >
              <option value="all">Tous</option>
              <option value="confirmé">Confirmé</option>
              <option value="terminé">Terminé</option>
              <option value="annulé">Annulé</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Période</label>
            <select 
              value={rdvFilters.dateRange}
              onChange={(e) => setRdvFilters({...rdvFilters, dateRange: e.target.value})}
            >
              <option value="all">Tous</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Structure</label>
            <select 
              value={rdvFilters.structure}
              onChange={(e) => setRdvFilters({...rdvFilters, structure: e.target.value})}
            >
              <option value="all">Toutes</option>
              {affiliatedStructures.map(structure => (
                <option key={structure.id} value={structure.id}>
                  {structure.nom}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      
      {/* Contenu principal */}
      <main className="mobile-content">
        {/* Vue détaillée */}
        {showDetailView && (
          <div className="mobile-detail-view">
            <div className="mobile-detail-header">
              <button className="btn-back" onClick={() => setShowDetailView(false)}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <div className="detail-title">
                {detailType === 'rdv' && 'Détails du rendez-vous'}
                {detailType === 'patient' && 'Fiche patient'}
                {detailType === 'structure' && 'Détails de la structure'}
                {detailType === 'backup' && 'Détails de la sauvegarde'}
              </div>
            </div>
            
            <div className="mobile-detail-content">
              {/* Détails du rendez-vous */}
              {detailType === 'rdv' && detailItem && (
                <div className="mobile-detail-card">
                  <h4>Rendez-vous</h4>
                  
                  <div className="detail-info">
                    <div className="detail-field">
                      <div className="field-label">Patient</div>
                      <div className="field-value">{detailItem.patientNom}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Date</div>
                      <div className="field-value">{new Date(detailItem.date).toLocaleDateString()}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Heure</div>
                      <div className="field-value">{detailItem.heure}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Durée</div>
                      <div className="field-value">{detailItem.duree} minutes</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Motif</div>
                      <div className="field-value">{detailItem.motif || 'Non spécifié'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Notes</div>
                      <div className="field-value">{detailItem.notes || 'Aucune note'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Statut</div>
                      <div className="field-value">
                        <span className={`status-badge status-${detailItem.statut}`}>
                          {detailItem.statut}
                        </span>
                      </div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Structure</div>
                      <div className="field-value">
                        {affiliatedStructures.find(s => s.id === detailItem.structureId)?.nom || 'Non spécifiée'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="detail-actions">
                    <button 
                      className="action-btn btn-edit"
                      onClick={() => {
                        // Logique pour éditer le rendez-vous
                        setNewRdv({
                          patientId: detailItem.patientId,
                          date: detailItem.date,
                          heure: detailItem.heure,
                          duree: detailItem.duree,
                          motif: detailItem.motif,
                          notes: detailItem.notes,
                          statut: detailItem.statut,
                          structureId: detailItem.structureId
                        });
                        setShowRdvModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      <span>Modifier</span>
                    </button>
                    
                    <button 
                      className="action-btn btn-delete"
                      onClick={() => {
                        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ?')) {
                          deleteRendezVous(detailItem.id);
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </div>
              )}
              
              {/* Détails du patient */}
              {detailType === 'patient' && detailItem && (
                <div className="mobile-detail-card">
                  <h4>Patient</h4>
                  
                  <div className="detail-info">
                    <div className="detail-field">
                      <div className="field-label">Nom</div>
                      <div className="field-value">{detailItem.nom}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Prénom</div>
                      <div className="field-value">{detailItem.prenom}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Date de naissance</div>
                      <div className="field-value">
                        {detailItem.dateNaissance ? new Date(detailItem.dateNaissance).toLocaleDateString() : 'Non spécifiée'}
                        {detailItem.dateNaissance && ` (${calculateAge(detailItem.dateNaissance)} ans)`}
                      </div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Téléphone</div>
                      <div className="field-value">{detailItem.telephone || 'Non spécifié'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Email</div>
                      <div className="field-value">{detailItem.email || 'Non spécifié'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Adresse</div>
                      <div className="field-value">{detailItem.adresse || 'Non spécifiée'}</div>
                    </div>
                    
                    <div className="detail-field">
                    <div className="field-value">{detailItem.adresse || 'Non spécifiée'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Diagnostics</div>
                      <div className="field-value">{detailItem.diagnostics || 'Aucun diagnostic'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Notes</div>
                      <div className="field-value">{detailItem.notes || 'Aucune note'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Statut</div>
                      <div className="field-value">
                        <span className={`status-badge status-${detailItem.statut}`}>
                          {detailItem.statut}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="patient-history">
                    <h5>Historique des rendez-vous</h5>
                    <div className="history-list">
                      {rendezvous
                        .filter(rdv => rdv.patientId === detailItem.id)
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map(rdv => (
                          <div key={rdv.id} className="history-item">
                            <div className="history-date">
                              <FontAwesomeIcon icon={faCalendarAlt} />
                              <span>{new Date(rdv.date).toLocaleDateString()} à {rdv.heure}</span>
                            </div>
                            <div className="history-motif">{rdv.motif || 'Sans motif'}</div>
                            <div className="history-status">
                              <span className={`status-badge status-${rdv.statut}`}>
                                {rdv.statut}
                              </span>
                            </div>
                          </div>
                        ))}
                      
                      {rendezvous.filter(rdv => rdv.patientId === detailItem.id).length === 0 && (
                        <div className="no-data">Aucun rendez-vous</div>
                      )}
                    </div>
                  </div>
                  
                  <div className="detail-actions">
                    <button 
                      className="action-btn btn-edit"
                      onClick={() => {
                        // Logique pour éditer le patient
                        setNewPatient({
                          nom: detailItem.nom,
                          prenom: detailItem.prenom,
                          dateNaissance: detailItem.dateNaissance,
                          telephone: detailItem.telephone,
                          email: detailItem.email,
                          adresse: detailItem.adresse,
                          diagnostics: detailItem.diagnostics,
                          notes: detailItem.notes,
                          statut: detailItem.statut
                        });
                        setShowPatientModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      <span>Modifier</span>
                    </button>
                    
                    <button 
                      className="action-btn btn-message"
                      onClick={() => {
                        // Trouver ou créer une conversation avec ce patient
                        const existingConv = conversations.find(
                          conv => conv.participants.includes(detailItem.id)
                        );
                        
                        if (existingConv) {
                          setSelectedConversation(existingConv);
                          setActiveTab('messagerie');
                          setShowDetailView(false);
                        } else {
                          // Créer une nouvelle conversation
                          addDoc(collection(db, 'conversations'), {
                            participants: [auth.currentUser.uid, detailItem.id],
                            lastMessage: "Nouvelle conversation",
                            lastMessageAt: serverTimestamp(),
                            unreadBy: []
                          }).then(docRef => {
                            loadConversations().then(() => {
                              const newConv = conversations.find(
                                conv => conv.id === docRef.id
                              );
                              if (newConv) {
                                setSelectedConversation(newConv);
                                setActiveTab('messagerie');
                                setShowDetailView(false);
                              }
                            });
                          });
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faComment} />
                      <span>Message</span>
                    </button>
                    
                    <button 
                      className="action-btn btn-delete"
                      onClick={() => {
                        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce patient ? Cette action est irréversible.')) {
                          deletePatient(detailItem.id);
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </div>
              )}
              
              {/* Détails de la structure */}
              {detailType === 'structure' && detailItem && (
                <div className="mobile-detail-card">
                  <h4>Structure</h4>
                  
                  <div className="detail-info">
                    <div className="detail-field">
                      <div className="field-label">Nom</div>
                      <div className="field-value">{detailItem.nom}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Type</div>
                      <div className="field-value">{detailItem.type || 'Non spécifié'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Adresse</div>
                      <div className="field-value">{detailItem.adresse || 'Non spécifiée'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Téléphone</div>
                      <div className="field-value">{detailItem.telephone || 'Non spécifié'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Email</div>
                      <div className="field-value">{detailItem.email || 'Non spécifié'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Description</div>
                      <div className="field-value">{detailItem.description || 'Aucune description'}</div>
                    </div>
                  </div>
                  
                  <div className="detail-actions">
                    {!affiliatedStructures.some(s => s.id === detailItem.id) && (
                      <button 
                        className="action-btn btn-affiliate"
                        onClick={() => requestAffiliation(detailItem.id)}
                      >
                        <FontAwesomeIcon icon={faLink} />
                        <span>Demander une affiliation</span>
                      </button>
                    )}
                    
                    {affiliatedStructures.some(s => s.id === detailItem.id) && (
                      <button 
                        className="action-btn btn-unaffiliate"
                        onClick={() => {
                          if (window.confirm('Êtes-vous sûr de vouloir vous désaffilier de cette structure ?')) {
                            removeAffiliation(detailItem.id);
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faUnlink} />
                        <span>Se désaffilier</span>
                      </button>
                    )}
                    
                    {pendingAffiliations.some(req => req.structureId === detailItem.id) && (
                      <button 
                        className="action-btn btn-cancel"
                        onClick={() => {
                          const request = pendingAffiliations.find(req => req.structureId === detailItem.id);
                          if (request && window.confirm('Annuler la demande d\'affiliation ?')) {
                            cancelAffiliationRequest(request.id);
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>Annuler la demande</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Détails de la sauvegarde */}
              {detailType === 'backup' && detailItem && (
                <div className="mobile-detail-card">
                  <h4>Sauvegarde</h4>
                  
                  <div className="detail-info">
                    <div className="detail-field">
                      <div className="field-label">Nom</div>
                      <div className="field-value">{detailItem.name}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Description</div>
                      <div className="field-value">{detailItem.description || 'Aucune description'}</div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Date de création</div>
                      <div className="field-value">
                        {detailItem.createdAt ? new Date(detailItem.createdAt.toDate()).toLocaleString() : 'Inconnue'}
                      </div>
                    </div>
                    
                    <div className="detail-field">
                      <div className="field-label">Nombre de patients</div>
                      <div className="field-value">{detailItem.patientsCount || 0}</div>
                    </div>
                  </div>
                  
                  <div className="backup-patients">
                    <h5>Patients sauvegardés</h5>
                    <div className="patients-list">
                      {detailItem.patientsData && detailItem.patientsData.map(patient => (
                        <div key={patient.id} className="patient-item">
                          <div className="patient-name">
                            {patient.prenom} {patient.nom}
                          </div>
                          {patient.dateNaissance && (
                            <div className="patient-age">
                              {calculateAge(patient.dateNaissance)} ans
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {(!detailItem.patientsData || detailItem.patientsData.length === 0) && (
                        <div className="no-data">Aucun patient</div>
                      )}
                    </div>
                  </div>
                  
                  <div className="detail-actions">
                    <button 
                      className="action-btn btn-restore"
                      onClick={() => {
                        if (window.confirm('Êtes-vous sûr de vouloir restaurer cette sauvegarde ? Cela pourrait écraser des données existantes.')) {
                          restoreBackup(detailItem.id);
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faRedo} />
                      <span>Restaurer</span>
                    </button>
                    
                    <button 
                      className="action-btn btn-delete"
                      onClick={() => {
                        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette sauvegarde ?')) {
                          deleteBackup(detailItem.id);
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Onglet Rendez-vous */}
        {!showDetailView && activeTab === 'rendezvous' && (
          <div className="tab-content">
            <div className="calendar-controls">
              <div className="calendar-nav">
                <button onClick={() => {
                  const newDate = new Date(currentDate);
                  if (calendarView === 'month') {
                    newDate.setMonth(newDate.getMonth() - 1);
                  } else if (calendarView === 'week') {
                    newDate.setDate(newDate.getDate() - 7);
                  }
                  setCurrentDate(newDate);
                }}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                
                <div className="calendar-title">
                  {calendarView === 'month' ? (
                    `${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`
                  ) : (
                    `Semaine du ${getWeekDays()[0].toLocaleDateString()} au ${getWeekDays()[6].toLocaleDateString()}`
                  )}
                </div>
                
                <button onClick={() => {
                  const newDate = new Date(currentDate);
                  if (calendarView === 'month') {
                    newDate.setMonth(newDate.getMonth() + 1);
                  } else if (calendarView === 'week') {
                    newDate.setDate(newDate.getDate() + 7);
                  }
                  setCurrentDate(newDate);
                }}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
              
              <div className="calendar-view-toggle">
                <button 
                  className={`view-btn ${calendarView === 'month' ? 'active' : ''}`}
                  onClick={() => setCalendarView('month')}
                >
                  <FontAwesomeIcon icon={faCalendarDay} />
                </button>
                <button 
                  className={`view-btn ${calendarView === 'week' ? 'active' : ''}`}
                  onClick={() => setCalendarView('week')}
                >
                  <FontAwesomeIcon icon={faCalendarWeek} />
                </button>
              </div>
            </div>
            
            {/* Vue mensuelle */}
            {calendarView === 'month' && (
              <div className="calendar-month">
                <div className="calendar-weekdays">
                  <div className="calendar-day-name">D</div>
                  <div className="calendar-day-name">L</div>
                  <div className="calendar-day-name">M</div>
                  <div className="calendar-day-name">M</div>
                  <div className="calendar-day-name">J</div>
                  <div className="calendar-day-name">V</div>
                  <div className="calendar-day-name">S</div>
                </div>
                
                <div className="calendar-days">
                  {generateCalendarDays().map((day, index) => {
                    const dateString = `${day.year}-${String(day.month + 1).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
                    const dayRdvs = rendezvous.filter(rdv => rdv.date === dateString);
                    const isToday = new Date().toISOString().split('T')[0] === dateString;
                    
                    return (
                      <div 
                        key={index}
                        className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                        onClick={() => {
                          setSelectedDate(new Date(day.year, day.month, day.day));
                          setCalendarView('day');
                        }}
                      >
                        <div className="day-number">{day.day}</div>
                        {dayRdvs.length > 0 && (
                          <div className="day-events">
                            {dayRdvs.length > 2 ? (
                              <div className="event-count">{dayRdvs.length} RDV</div>
                            ) : (
                              dayRdvs.slice(0, 2).map(rdv => (
                                <div 
                                  key={rdv.id}
                                  className={`event-dot status-${rdv.statut}`}
                                ></div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Vue hebdomadaire */}
            {calendarView === 'week' && (
              <div className="calendar-week">
                <div className="week-header">
                  {getWeekDays().map((day, index) => (
                    <div key={index} className="week-day-header">
                      <div className="week-day-name">
                        {day.toLocaleDateString('default', { weekday: 'short' })}
                      </div>
                      <div className={`week-day-number ${day.toDateString() === new Date().toDateString() ? 'today' : ''}`}>
                        {day.getDate()}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="week-events">
                  {getWeekDays().map((day, index) => {
                    const dateString = day.toISOString().split('T')[0];
                    const dayRdvs = rendezvous.filter(rdv => rdv.date === dateString);
                    
                    return (
                      <div key={index} className="week-day-events">
                        {dayRdvs.length > 0 ? (
                          dayRdvs
                            .sort((a, b) => a.heure.localeCompare(b.heure))
                            .map(rdv => (
                              <div 
                                key={rdv.id}
                                className={`week-event status-${rdv.statut}`}
                                onClick={() => showDetails('rdv', rdv)}
                              >
                                <div className="event-time">{rdv.heure}</div>
                                <div className="event-title">{rdv.patientNom}</div>
                                {rdv.motif && (
                                  <div className="event-motif">{rdv.motif}</div>
                                )}
                              </div>
                            ))
                        ) : (
                          <div className="no-events">Aucun RDV</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Vue journalière */}
            {calendarView === 'day' && (
              <div className="calendar-day-view">
                <div className="day-header">
                  <button className="btn-back" onClick={() => setCalendarView('month')}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </button>
                  <div className="day-title">
                    {selectedDate.toLocaleDateString('default', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </div>
                </div>
                
                <div className="day-events">
                  {(() => {
                    const dateString = selectedDate.toISOString().split('T')[0];
                    const dayRdvs = rendezvous.filter(rdv => rdv.date === dateString);
                    
                    if (dayRdvs.length === 0) {
                      return <div className="no-events">Aucun rendez-vous ce jour</div>;
                    }
                    
                    return dayRdvs
                      .sort((a, b) => a.heure.localeCompare(b.heure))
                      .map(rdv => (
                        <div 
                          key={rdv.id}
                          className={`day-event status-${rdv.statut}`}
                          onClick={() => showDetails('rdv', rdv)}
                        >
                          <div className="event-time">
                            <FontAwesomeIcon icon={faClock} />
                            <span>{rdv.heure} ({rdv.duree} min)</span>
                          </div>
                          <div className="event-patient">{rdv.patientNom}</div>
                          {rdv.motif && (
                            <div className="event-motif">{rdv.motif}</div>
                          )}
                          {rdv.structureId && (
                            <div className="event-location">
                              <FontAwesomeIcon icon={faClinicMedical} />
                              <span>
                                {affiliatedStructures.find(s => s.id === rdv.structureId)?.nom || 'Structure inconnue'}
                              </span>
                            </div>
                          )}
                        </div>
                      ));
                  })()}
                </div>
              </div>
            )}
            
            {/* Liste des rendez-vous */}
            {calendarView !== 'day' && (
              <div className="rdv-list">
                <h4>Prochains rendez-vous</h4>
                
                {filteredRendezVous.length > 0 ? (
                  filteredRendezVous
                    .filter(rdv => new Date(rdv.date) >= new Date().setHours(0, 0, 0, 0))
                    .sort((a, b) => {
                      // Trier par date puis par heure
                      const dateA = new Date(a.date + 'T' + a.heure);
                      const dateB = new Date(b.date + 'T' + b.heure);
                      return dateA - dateB;
                    })
                    .slice(0, 5)
                    .map(rdv => (
                      <div 
                        key={rdv.id}
                        className="rdv-item"
                        onClick={() => showDetails('rdv', rdv)}
                      >
                        <div className="rdv-date">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>{new Date(rdv.date).toLocaleDateString()}</span>
                        </div>
                        <div className="rdv-time">
                          <FontAwesomeIcon icon={faClock} />
                          <span>{rdv.heure}</span>
                        </div>
                        <div className="rdv-patient">{rdv.patientNom}</div>
                        <div className="rdv-status">
                          <span className={`status-badge status-${rdv.statut}`}>
                            {rdv.statut}
                          </span>
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="no-data">Aucun rendez-vous à venir</div>
                )}
                
                <button 
                  className="btn-all"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FontAwesomeIcon icon={faFilter} />
                  <span>Filtrer</span>
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Onglet Patients */}
        {!showDetailView && activeTab === 'patients' && (
          <div className="tab-content">
            <div className="patient-filters">
              <div className="filter-group">
                <label>Trier par</label>
                <select 
                  value={patientFilters.sortBy}
                  onChange={(e) => setPatientFilters({...patientFilters, sortBy: e.target.value})}
                >
                  <option value="name">Nom</option>
                  <option value="date">Date d'ajout</option>
                </select>
              </div>
              
              <div className="filter-toggle">
                <label>
                  <input 
                    type="checkbox"
                    checked={patientFilters.onlyActive}
                    onChange={(e) => setPatientFilters({...patientFilters, onlyActive: e.target.checked})}
                  />
                  <span>Patients actifs uniquement</span>
                </label>
              </div>
            </div>
            
            <div className="patients-list">
              {filteredPatients.length > 0 ? (
                filteredPatients.map(patient => (
                  <div 
                    key={patient.id}
                    className="patient-card"
                    onClick={() => showDetails('patient', patient)}
                  >
                    <div className="patient-info">
                      <div className="patient-name">
                        {patient.prenom} {patient.nom}
                      </div>
                      
                      {patient.dateNaissance && (
                        <div className="patient-age">
                          {calculateAge(patient.dateNaissance)} ans
                        </div>
                      )}
                      
                      {patient.telephone && (
                        <div className="patient-phone">
                          <FontAwesomeIcon icon={faPhone} />
                          <span>{patient.telephone}</span>
                        </div>
                      )}
                      
                      {patient.email && (
                        <div className="patient-email">
                          <FontAwesomeIcon icon={faEnvelope} />
                          <span>{patient.email}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="patient-status">
                      <span className={`status-badge status-${patient.statut}`}>
                        {patient.statut}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data">Aucun patient trouvé</div>
              )}
            </div>
            
            <button 
              className="fab"
              onClick={() => setShowPatientModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        )}
        
        {/* Onglet Messagerie */}
        {!showDetailView && activeTab === 'messagerie' && (
          <div className="tab-content">
            {!selectedConversation ? (
              <div className="conversations-list">
                {filteredConversations.length > 0 ? (
                  filteredConversations
                    .sort((a, b) => {
                      // Trier par date du dernier message
                      const dateA = a.lastMessageAt ? new Date(a.lastMessageAt.toDate()) : new Date(0);
                      const dateB = b.lastMessageAt ? new Date(b.lastMessageAt.toDate()) : new Date(0);
                      return dateB - dateA;
                    })
                    .map(conv => (
                      <div 
                        key={conv.id}
                        className={`conversation-item ${conv.unreadCount > 0 ? 'unread' : ''}`}
                        onClick={() => {
                          setSelectedConversation(conv);
                          fetchMessages(conv.id);
                        }}
                      >
                        <div className="conversation-avatar">
                          {conv.patient.prenom?.charAt(0)}{conv.patient.nom?.charAt(0)}
                        </div>
                        
                        <div className="conversation-info">
                          <div className="conversation-name">
                            {conv.patient.prenom} {conv.patient.nom}
                          </div>
                          
                          <div className="conversation-preview">
                            {conv.lastMessage || 'Nouvelle conversation'}
                          </div>
                        </div>
                        
                        <div className="conversation-meta">
                          {conv.lastMessageAt && (
                            <div className="conversation-time">
                              {new Date(conv.lastMessageAt.toDate()).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          )}
                          
                          {conv.unreadCount > 0 && (
                            <div className="conversation-badge">
                              {conv.unreadCount}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="no-data">Aucune conversation</div>
                )}
              </div>
            ) : (
              <div className="conversation-view">
                <div className="conversation-header">
                  <button 
                    className="btn-back"
                    onClick={() => {
                      setSelectedConversation(null);
                      setMessages([]);
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </button>
                  
                  <div className="conversation-title">
                    {selectedConversation.patient.prenom} {selectedConversation.patient.nom}
                  </div>
                </div>
                
                <div className="messages-container">
                  {messages.length > 0 ? (
                    <div className="messages-list">
                      {messages.map(msg => (
                        <div 
                          key={msg.id}
                          className={`message ${msg.senderId === auth.currentUser.uid ? 'sent' : 'received'}`}
                        >
                          {msg.text && (
                            <div className="message-text">{msg.text}</div>
                          )}
                          
                          {msg.attachment && (
                            <div className="message-attachment">
                              {msg.attachment.type.startsWith('image/') ? (
                                <img 
                                  src={msg.attachment.url}
                                  alt="Pièce jointe"
                                  onClick={() => window.open(msg.attachment.url, '_blank')}
                                />
                              ) : (
                                <div 
                                  className="file-attachment"
                                  onClick={() => window.open(msg.attachment.url, '_blank')}
                                >
                                  <FontAwesomeIcon icon={faFile} />
                                  <span>{msg.attachment.name}</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {msg.audioAttachment && (
                            <div className="message-audio">
                              <audio controls src={msg.audioAttachment.url}></audio>
                              <div className="audio-info">
                                <FontAwesomeIcon icon={faMicrophone} />
                                <span>
                                  {Math.floor(msg.audioAttachment.duration / 60)}:
                                  {String(msg.audioAttachment.duration % 60).padStart(2, '0')}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          <div className="message-time">
                            {msg.timestamp ? new Date(msg.timestamp.toDate()).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'En cours...'}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  ) : (
                    <div className="no-messages">
                      <div className="no-messages-icon">
                        <FontAwesomeIcon icon={faComments} />
                      </div>
                      <div className="no-messages-text">
                        Commencez la conversation avec {selectedConversation.patient.prenom}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="message-composer">
                  {isRecording ? (
                    <div className="recording-container">
                      <div className="recording-indicator">
                        <FontAwesomeIcon icon={faMicrophone} pulse />
                        <span>{formatRecordingTime(recordingTime)}</span>
                      </div>
                      <div className="recording-actions">
                        <button className="btn-stop" onClick={stopRecording}>
                          <FontAwesomeIcon icon={faStop} />
                        </button>
                        <button className="btn-cancel" onClick={cancelRecording}>
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {audioBlob ? (
                        <div className="audio-preview">
                          <audio controls src={URL.createObjectURL(audioBlob)}></audio>
                          <button className="btn-cancel" onClick={() => setAudioBlob(null)}>
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button className="btn-attach" onClick={() => document.getElementById('file-input').click()}>
                            <FontAwesomeIcon icon={faPaperclip} />
                            <input 
                              id="file-input" 
                              type="file" 
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  setAttachmentFile(e.target.files[0]);
                                }
                              }}
                            />
                          </button>
                          
                          <div className="message-input">
                            <input 
                              type="text" 
                              placeholder="Votre message..." 
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  sendMessage(e);
                                }
                              }}
                            />
                          </div>
                          
                          <button className="btn-record" onClick={startRecording}>
                            <FontAwesomeIcon icon={faMicrophone} />
                          </button>
                        </>
                      )}
                      
                      {(newMessage.trim() || attachmentFile || audioBlob) ? (
                        <button className="btn-send" onClick={sendMessage}>
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </button>
                      ) : (
                        <button className="btn-send disabled">
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </button>
                      )}
                    </>
                  )}
                  
                  {attachmentFile && (
                    <div className="attachment-preview">
                      <div className="attachment-name">
                        <FontAwesomeIcon icon={faFileImage} />
                        <span>{attachmentFile.name}</span>
                      </div>
                      <button className="btn-remove" onClick={() => setAttachmentFile(null)}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Onglet Affiliations */}
        {!showDetailView && activeTab === 'affiliations' && (
          <div className="tab-content">
            <div className="affiliation-tabs">
              <button 
                className={`tab-btn ${activeSubTab === 'affiliated' ? 'active' : ''}`}
                onClick={() => setActiveSubTab('affiliated')}
              >
                Mes structures
              </button>
              <button 
                className={`tab-btn ${activeSubTab === 'pending' ? 'active' : ''}`}
                onClick={() => setActiveSubTab('pending')}
              >
                En attente
              </button>
              <button 
                className={`tab-btn ${activeSubTab === 'available' ? 'active' : ''}`}
                onClick={() => setActiveSubTab('available')}
              >
                Disponibles
              </button>
            </div>
            
            {/* Structures affiliées */}
            {activeSubTab === 'affiliated' && (
              <div className="structures-list">
                {affiliatedStructures.length > 0 ? (
                  affiliatedStructures.map(structure => (
                    <div 
                      key={structure.id}
                      className="structure-card"
                      onClick={() => showDetails('structure', structure)}
                    >
                      <div className="structure-info">
                        <div className="structure-name">{structure.nom}</div>
                        <div className="structure-type">{structure.type || 'Structure médicale'}</div>
                        
                        {structure.adresse && (
                          <div className="structure-address">
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                            <span>{structure.adresse}</span>
                          </div>
                        )}
                        
                        {structure.telephone && (
                          <div className="structure-phone">
                            <FontAwesomeIcon icon={faPhone} />
                            <span>{structure.telephone}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="structure-actions">
                        <button 
                          className="btn-unaffiliate"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Êtes-vous sûr de vouloir vous désaffilier de cette structure ?')) {
                              removeAffiliation(structure.id);
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faUnlink} />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-data">Aucune structure affiliée</div>
                )}
              </div>
            )}
            
            {/* Demandes en attente */}
            {activeSubTab === 'pending' && (
              <div className="structures-list">
                {pendingAffiliations.length > 0 ? (
                  pendingAffiliations.map(request => (
                    <div 
                      key={request.id}
                      className="structure-card pending"
                    >
                      <div className="structure-info">
                        <div className="structure-name">{request.structureName}</div>
                        <div className="structure-status">
                          <FontAwesomeIcon icon={faClock} />
                          <span>En attente d'approbation</span>
                        </div>
                        
                        {request.createdAt && (
                          <div className="request-date">
                            Demandé le {new Date(request.createdAt.toDate()).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      
                      <div className="structure-actions">
                        <button 
                          className="btn-cancel"
                          onClick={() => {
                            if (window.confirm('Annuler cette demande d\'affiliation ?')) {
                              cancelAffiliationRequest(request.id);
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-data">Aucune demande en attente</div>
                )}
              </div>
            )}
            
            {/* Structures disponibles */}
            {activeSubTab === 'available' && (
              <div className="structures-list">
                {filteredStructures
                  .filter(structure => 
                    !affiliatedStructures.some(s => s.id === structure.id) &&
                    !pendingAffiliations.some(req => req.structureId === structure.id)
                  )
                  .map(structure => (
                    <div 
                      key={structure.id}
                      className="structure-card"
                      onClick={() => showDetails('structure', structure)}
                    >
                      <div className="structure-info">
                        <div className="structure-name">{structure.nom}</div>
                        <div className="structure-type">{structure.type || 'Structure médicale'}</div>
                        
                        {structure.adresse && (
                          <div className="structure-address">
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                            <span>{structure.adresse}</span>
                          </div>
                        )}
                        
                        {structure.telephone && (
                          <div className="structure-phone">
                            <FontAwesomeIcon icon={faPhone} />
                            <span>{structure.telephone}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="structure-actions">
                        <button 
                          className="btn-affiliate"
                          onClick={(e) => {
                            e.stopPropagation();
                            requestAffiliation(structure.id);
                          }}
                        >
                          <FontAwesomeIcon icon={faLink} />
                        </button>
                      </div>
                    </div>
                  ))}
                
                {filteredStructures.filter(structure => 
                  !affiliatedStructures.some(s => s.id === structure.id) &&
                  !pendingAffiliations.some(req => req.structureId === structure.id)
                ).length === 0 && (
                  <div className="no-data">Aucune structure disponible</div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Onglet Profil */}
        {!showDetailView && activeTab === 'profil' && (
          <div className="tab-content">
            <div className="profile-card">
              <div className="profile-header">
                <div className="profile-avatar">
                  {medecinData?.prenom?.charAt(0)}{medecinData?.nom?.charAt(0)}
                </div>
                <div className="profile-name">
                  Dr. {medecinData?.prenom} {medecinData?.nom}
                </div>
                <div className="profile-specialty">
                  {medecinData?.specialite}
                </div>
              </div>
              
              <div className="profile-section">
                <h4>Informations personnelles</h4>
                
                <div className="profile-field">
                  <div className="field-label">Email</div>
                  <div className="field-value">{auth.currentUser?.email}</div>
                </div>
                
                <div className="profile-field">
                  <div className="field-label">Téléphone</div>
                  <div className="field-value">{medecinData?.telephone || 'Non spécifié'}</div>
                </div>
                
                <div className="profile-field">
                  <div className="field-label">Adresse</div>
                  <div className="field-value">{medecinData?.adresse || 'Non spécifiée'}</div>
                </div>
              </div>
              
              <div className="profile-section">
                <h4>Disponibilités</h4>
                
                <div className="availability-list">
                  {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].map(day => {
                    const dispo = medecinData?.disponibilites?.[day] || { actif: false, debut: '09:00', fin: '17:00' };
                    
                    return (
                      <div key={day} className="availability-item">
                        <div className="day-name">{day.charAt(0).toUpperCase() + day.slice(1)}</div>
                        
                        <div className="day-toggle">
                          <label className="toggle-switch">
                            <input 
                              type="checkbox"
                              checked={dispo.actif}
                              onChange={(e) => updateAvailability(day, e.target.checked, dispo.debut, dispo.fin)}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                        
                        <div className="day-hours">
                          <input 
                            type="time"
                            value={dispo.debut}
                            onChange={(e) => updateAvailability(day, dispo.actif, e.target.value, dispo.fin)}
                            disabled={!dispo.actif}
                          />
                          <span>-</span>
                          <input 
                            type="time"
                            value={dispo.fin}
                            onChange={(e) => updateAvailability(day, dispo.actif, dispo.debut, e.target.value)}
                            disabled={!dispo.actif}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="profile-actions">
                <button 
                  className="btn-password"
                  onClick={() => setShowPasswordModal(true)}
                >
                  <FontAwesomeIcon icon={faKey} />
                  <span>Changer le mot de passe</span>
                </button>
                
                <button 
                  className="btn-logout"
                  onClick={handleLogout}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Onglet Sauvegardes */}
        {!showDetailView && activeTab === 'sauvegardes' && (
          <div className="tab-content">
            <div className="backups-list">
              {filteredBackups.length > 0 ? (
                filteredBackups.map(backup => (
                  <div 
                    key={backup.id}
                    className="backup-card"
                    onClick={() => showDetails('backup', backup)}
                  >
                    <div className="backup-info">
                      <div className="backup-name">{backup.name}</div>
                      
                      {backup.description && (
                        <div className="backup-description">{backup.description}</div>
                      )}
                      
                      <div className="backup-meta">
                        <div className="backup-date">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>
                            {backup.createdAt ? new Date(backup.createdAt.toDate()).toLocaleDateString() : 'Date inconnue'}
                          </span>
                        </div>
                        
                        <div className="backup-count">
                          <FontAwesomeIcon icon={faUser} />
                          <span>{backup.patientsCount || 0} patients</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="backup-actions">
                      <button 
                        className="btn-restore"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Êtes-vous sûr de vouloir restaurer cette sauvegarde ?')) {
                            restoreBackup(backup.id);
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faRedo} />
                      </button>
                      
                      <button 
                        className="btn-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Êtes-vous sûr de vouloir supprimer cette sauvegarde ?')) {
                            deleteBackup(backup.id);
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data">Aucune sauvegarde</div>
              )}
            </div>
            
            <button 
              className="fab"
              onClick={() => setShowBackupModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        )}
        
        {/* Onglet Paramètres */}
        {!showDetailView && activeTab === 'settings' && (
          <div className="tab-content">
            <div className="settings-card">
              <div className="settings-section">
                <h4>Paramètres du compte</h4>
                
                <div className="settings-item">
                  <div className="settings-label">
                    <FontAwesomeIcon icon={faKey} />
                    <span>Mot de passe</span>
                  </div>
                  <button 
                    className="btn-change"
                    onClick={() => setShowPasswordModal(true)}
                  >
                    Modifier
                  </button>
                </div>
                
                <div className="settings-item">
                  <div className="settings-label">
                    <FontAwesomeIcon icon={faDatabase} />
                    <span>Sauvegardes</span>
                  </div>
                  <button 
                    className="btn-navigate"
                    onClick={() => setActiveTab('sauvegardes')}
                  >
                    Gérer
                  </button>
                </div>
              </div>
              
              <div className="settings-section">
                <h4>Notifications</h4>
                
                <div className="settings-item">
                  <div className="settings-label">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>Rappels de rendez-vous</span>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                
                <div className="settings-item">
                  <div className="settings-label">
                    <FontAwesomeIcon icon={faComment} />
                    <span>Nouveaux messages</span>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                
                <div className="settings-item">
                  <div className="settings-label">
                    <FontAwesomeIcon icon={faLink} />
                    <span>Demandes d'affiliation</span>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
              
              <div className="settings-section">
                <h4>Confidentialité et sécurité</h4>
                
                <div className="settings-item">
                  <div className="settings-label">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    <span>Authentification à deux facteurs</span>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                
                <div className="settings-item">
                  <div className="settings-label">
                    <FontAwesomeIcon icon={faLock} />
                    <span>Verrouillage automatique</span>
                  </div>
                  <select defaultValue="10">
                    <option value="5">5 minutes</option>
                    <option value="10">10 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="0">Jamais</option>
                  </select>
                </div>
              </div>
              
              <div className="settings-section">
                <h4>À propos</h4>
                
                <div className="settings-item">
                  <div className="settings-label">
                    <FontAwesomeIcon icon={faInfoCircle} />
                    <span>Version</span>
                  </div>
                  <div className="settings-value">1.0.0</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Navigation du bas */}
      {!showDetailView && (
        <nav className="mobile-nav">
          <button 
            className={`nav-item ${activeTab === 'rendezvous' ? 'active' : ''}`}
            onClick={() => setActiveTab('rendezvous')}
          >
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>RDV</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'patients' ? 'active' : ''}`}
            onClick={() => setActiveTab('patients')}
          >
            <FontAwesomeIcon icon={faUser} />
            <span>Patients</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'messagerie' ? 'active' : ''}`}
            onClick={() => setActiveTab('messagerie')}
          >
            <div className="nav-icon">
              <FontAwesomeIcon icon={faComment} />
              {unreadMessagesCount > 0 && (
                <div className="nav-badge">{unreadMessagesCount}</div>
              )}
            </div>
            <span>Messages</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'profil' ? 'active' : ''}`}
            onClick={() => setActiveTab('profil')}
          >
            <FontAwesomeIcon icon={faUserMd} />
            <span>Profil</span>
          </button>
        </nav>
      )}
      
      {/* Modales */}
      {/* Modal de changement de mot de passe */}
      {showPasswordModal && (
        <div className="mobile-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h4>Changer le mot de passe</h4>
              <button className="btn-close" onClick={() => setShowPasswordModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input 
                  type="password"
                  value={newPasswordValue}
                  onChange={(e) => setNewPasswordValue(e.target.value)}
                  placeholder="Nouveau mot de passe"
                />
              </div>
              
              <div className="form-group">
                <label>Confirmer le mot de passe</label>
                <input 
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmer le mot de passe"
                />
              </div>
              
              {passwordError && (
                <div className="form-error">{passwordError}</div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setShowPasswordModal(false)}
              >
                Annuler
              </button>
              <button 
                className="btn-save"
                onClick={handlePasswordChange}
                disabled={!newPasswordValue || !confirmPassword}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de sauvegarde */}
      {showBackupModal && (
        <div className="mobile-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h4>Nouvelle sauvegarde</h4>
              <button className="btn-close" onClick={() => setShowBackupModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Nom de la sauvegarde</label>
                <input 
                  type="text"
                  value={backupName}
                  onChange={(e) => setBackupName(e.target.value)}
                  placeholder="Nom de la sauvegarde"
                />
              </div>
              
              <div className="form-group">
                <label>Description (optionnelle)</label>
                <textarea 
                  value={backupDescription}
                  onChange={(e) => setBackupDescription(e.target.value)}
                  placeholder="Description de la sauvegarde"
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Sélectionner les patients</label>
                <div className="patients-select">
                  {patients.map(patient => (
                    <div key={patient.id} className="patient-option">
                      <label>
                        <input 
                          type="checkbox"
                          checked={selectedPatientIds.includes(patient.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPatientIds([...selectedPatientIds, patient.id]);
                            } else {
                              setSelectedPatientIds(selectedPatientIds.filter(id => id !== patient.id));
                            }
                          }}
                        />
                        <span>{patient.prenom} {patient.nom}</span>
                      </label>
                    </div>
                  ))}
                  
                  {patients.length === 0 && (
                    <div className="no-data">Aucun patient disponible</div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setShowBackupModal(false)}
              >
                Annuler
              </button>
              <button 
                className="btn-save"
                onClick={createBackup}
                disabled={!backupName || selectedPatientIds.length === 0}
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de rendez-vous */}
      {showRdvModal && (
        <div className="mobile-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h4>{newRdv.id ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}</h4>
              <button className="btn-close" onClick={() => setShowRdvModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Patient</label>
                <select 
                  value={newRdv.patientId}
                  onChange={(e) => setNewRdv({...newRdv, patientId: e.target.value})}
                >
                  <option value="">Sélectionner un patient</option>
                  {patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.prenom} {patient.nom}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Date</label>
                <input 
                  type="date"
                  value={newRdv.date}
                  onChange={(e) => setNewRdv({...newRdv, date: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Heure</label>
                <input 
                  type="time"
                  value={newRdv.heure}
                  onChange={(e) => setNewRdv({...newRdv, heure: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Durée (minutes)</label>
                <select 
                  value={newRdv.duree}
                  onChange={(e) => setNewRdv({...newRdv, duree: parseInt(e.target.value)})}
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 heure</option>
                  <option value="90">1 heure 30</option>
                  <option value="120">2 heures</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Motif</label>
                <input 
                  type="text"
                  value={newRdv.motif}
                  onChange={(e) => setNewRdv({...newRdv, motif: e.target.value})}
                  placeholder="Motif du rendez-vous"
                />
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea 
                  value={newRdv.notes}
                  onChange={(e) => setNewRdv({...newRdv, notes: e.target.value})}
                  placeholder="Notes additionnelles"
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Statut</label>
                <select 
                  value={newRdv.statut}
                  onChange={(e) => setNewRdv({...newRdv, statut: e.target.value})}
                >
                  <option value="confirmé">Confirmé</option>
                  <option value="en attente">En attente</option>
                  <option value="annulé">Annulé</option>
                  <option value="terminé">Terminé</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Structure</label>
                <select 
                  value={newRdv.structureId}
                  onChange={(e) => setNewRdv({...newRdv, structureId: e.target.value})}
                >
                  <option value="">Aucune structure</option>
                  {affiliatedStructures.map(structure => (
                    <option key={structure.id} value={structure.id}>
                      {structure.nom}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setShowRdvModal(false)}
              >
                Annuler
              </button>
              <button 
                className="btn-save"
                onClick={createRendezVous}
                disabled={!newRdv.patientId || !newRdv.date || !newRdv.heure}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de patient */}
      {showPatientModal && (
        <div className="mobile-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h4>{newPatient.id ? 'Modifier le patient' : 'Nouveau patient'}</h4>
              <button className="btn-close" onClick={() => setShowPatientModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Nom</label>
                <input 
                  type="text"
                  value={newPatient.nom}
                  onChange={(e) => setNewPatient({...newPatient, nom: e.target.value})}
                  placeholder="Nom du patient"
                />
              </div>
              
              <div className="form-group">
                <label>Prénom</label>
                <input 
                  type="text"
                  value={newPatient.prenom}
                  onChange={(e) => setNewPatient({...newPatient, prenom: e.target.value})}
                  placeholder="Prénom du patient"
                />
              </div>
              
              <div className="form-group">
                <label>Date de naissance</label>
                <input 
                  type="date"
                  value={newPatient.dateNaissance}
                  onChange={(e) => setNewPatient({...newPatient, dateNaissance: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Téléphone</label>
                <input 
                  type="tel"
                  value={newPatient.telephone}
                  onChange={(e) => setNewPatient({...newPatient, telephone: e.target.value})}
                  placeholder="Numéro de téléphone"
                />
              </div>
              
              <div className="form-group">
                <label>Email</label>
                <input 
                  type="email"
                  value={newPatient.email}
                  onChange={(e) => setNewPatient({...newPatient, email: e.target.value})}
                  placeholder="Adresse email"
                />
              </div>
              
              <div className="form-group">
                <label>Adresse</label>
                <input 
                  type="text"
                  value={newPatient.adresse}
                  onChange={(e) => setNewPatient({...newPatient, adresse: e.target.value})}
                  placeholder="Adresse postale"
                />
              </div>
              
              <div className="form-group">
                <label>Diagnostics</label>
                <textarea 
                  value={newPatient.diagnostics}
                  onChange={(e) => setNewPatient({...newPatient, diagnostics: e.target.value})}
                  placeholder="Diagnostics médicaux"
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea 
                  value={newPatient.notes}
                  onChange={(e) => setNewPatient({...newPatient, notes: e.target.value})}
                  placeholder="Notes additionnelles"
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Statut</label>
                <select 
                  value={newPatient.statut}
                  onChange={(e) => setNewPatient({...newPatient, statut: e.target.value})}
                >
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                  <option value="archive">Archivé</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setShowPatientModal(false)}
              >
                Annuler
              </button>
              <button 
                className="btn-save"
                onClick={createPatient}
                disabled={!newPatient.nom || !newPatient.prenom}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
      <style jsx>
        {`
        /* Styles de base */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: 'Roboto', sans-serif;
  background-color: #f8f9fa;
  color: #333;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Conteneur principal */
.mobile-dashboard {
  position: relative;
  max-width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

/* Écran de chargement */
.mobile-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 1000;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Header */
.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 10;
}

.mobile-menu-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #333;
  cursor: pointer;
  padding: 5px;
}

.mobile-header-title {
  font-size: 1.2rem;
  font-weight: 500;
}

.mobile-header-actions {
  display: flex;
  gap: 10px;
}

.header-action-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #3498db;
  cursor: pointer;
  padding: 5px;
}

/* Menu latéral */
.mobile-side-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 80%;
  max-width: 300px;
  height: 100%;
  background-color: #fff;
  z-index: 1000;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  transform: translateX(0);
  transition: transform 0.3s ease;
}

.side-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.side-menu-header {
  padding: 20px 15px;
  background-color: #3498db;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.side-menu-user {
  display: flex;
  align-items: center;
  gap: 10px;
}

.side-menu-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #fff;
  color: #3498db;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.side-menu-info {
  display: flex;
  flex-direction: column;
}

.side-menu-name {
  font-weight: 500;
  font-size: 1rem;
}

.side-menu-specialty {
  font-size: 0.8rem;
  opacity: 0.8;
}

.side-menu-close {
  background: none;
  border: none;
  color: #fff;
  font-size: 1.2rem;
  cursor: pointer;
}

.side-menu-content {
  padding: 15px;
}

.side-menu-section {
  margin-bottom: 20px;
}

.side-menu-section h4 {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.side-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 10px;
  border-radius: 5px;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  font-size: 0.95rem;
  color: #333;
  cursor: pointer;
  transition: background-color 0.2s;
}

.side-menu-item:hover {
  background-color: #f0f0f0;
}

.side-menu-logout {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 10px;
  border-radius: 5px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  width: 100%;
  text-align: left;
  font-size: 0.95rem;
  color: #dc3545;
  cursor: pointer;
  margin-top: 20px;
}

/* Alerte de message */
.mobile-alert {
  padding: 10px 15px;
  background-color: #d4edda;
  color: #155724;
  border-radius: 5px;
  margin: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-close {
  background: none;
  border: none;
  color: inherit;
  font-size: 1rem;
  cursor: pointer;
}

/* Barre de recherche */
.mobile-search {
  padding: 10px 15px;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
}

.search-input {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 10px;
  color: #999;
}

.search-input input {
  width: 100%;
  padding: 10px 10px 10px 35px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  font-size: 0.9rem;
}

/* Filtres */
.mobile-filters {
  padding: 10px 15px;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
}

.filter-group {
  margin-bottom: 10px;
}

.filter-group label {
  display: block;
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 5px;
}

.filter-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  font-size: 0.9rem;
}

/* Contenu principal */
.mobile-content {
  flex: 1;
  padding-bottom: 60px; /* Espace pour la navigation du bas */
}

.tab-content {
  padding: 10px;
}

/* Vue détaillée */
.mobile-detail-view {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #fff;
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.mobile-detail-header {
  display: flex;
  align-items: center;
  padding: 15px;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-back {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #333;
  cursor: pointer;
  padding: 5px;
  margin-right: 10px;
}

.detail-title {
  font-size: 1.1rem;
  font-weight: 500;
}

.mobile-detail-content {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
}

.mobile-detail-card {
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 15px;
  margin-bottom: 15px;
}

.mobile-detail-card h4 {
  margin-bottom: 15px;
  font-size: 1.1rem;
  color: #333;
}

.detail-info {
  margin-bottom: 20px;
}

.detail-field {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
}

.field-label {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 3px;
}

.field-value {
  font-size: 0.95rem;
}

.detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px;
  border-radius: 5px;
  border: none;
  font-size: 0.9rem;
  cursor: pointer;
  min-width: 100px;
}

.btn-edit {
  background-color: #3498db;
  color: #fff;
}

.btn-delete {
  background-color: #dc3545;
  color: #fff;
}

.btn-message {
  background-color: #28a745;
  color: #fff;
}

.btn-affiliate {
  background-color: #17a2b8;
  color: #fff;
}

.btn-unaffiliate {
  background-color: #6c757d;
  color: #fff;
}

.btn-restore {
  background-color: #ffc107;
  color: #333;
}

/* Navigation du bas */
.mobile-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-around;
  background-color: #fff;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 10;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background: none;
  border: none;
  color: #666;
  font-size: 0.7rem;
  cursor: pointer;
}

.nav-item.active {
  color: #3498db;
}

.nav-item svg {
  font-size: 1.2rem;
  margin-bottom: 3px;
}

.nav-icon {
  position: relative;
}

.nav-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #dc3545;
  color: #fff;
  font-size: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Modales */
.mobile-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  background-color: #fff;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h4 {
  font-size: 1.1rem;
  margin: 0;
}

.modal-body {
  padding: 15px;
  overflow-y: auto;
  max-height: 60vh;
}

.modal-footer {
  padding: 15px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-cancel {
  padding: 8px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  background-color: #fff;
  color: #666;
  cursor: pointer;
}

.btn-save {
  padding: 8px 15px;
  border: none;
  border-radius: 5px;
  background-color: #3498db;
  color: #fff;
  cursor: pointer;
}

.btn-save:disabled {
  background-color: #a0d0f0;
  cursor: not-allowed;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  margin-bottom: 5px;
  color: #333;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  font-size: 0.9rem;
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.form-error {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 5px;
}

/* Styles spécifiques aux onglets */
/* Rendez-vous */
.calendar-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.calendar-nav {
  display: flex;
  align-items: center;
  gap: 10px;
}

.calendar-nav button {
  background: none;
  border: none;
  font-size: 1rem;
  color: #333;
  cursor: pointer;
}

.calendar-title {
  font-weight: 500;
  font-size: 0.9rem;
}

.calendar-view-toggle {
  display: flex;
  gap: 5px;
}

.view-btn {
  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  padding: 5px 10px;
  cursor: pointer;
  color: #666;
}

.view-btn.active {
  background-color: #3498db;
  color: #fff;
  border-color: #3498db;
}

.calendar-month {
  margin-bottom: 20px;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-weight: 500;
  color: #666;
  font-size: 0.8rem;
  margin-bottom: 5px;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.calendar-day {
  aspect-ratio: 1;
  padding: 5px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.calendar-day.other-month {
  opacity: 0.3;
}

.calendar-day.today {
  background-color: #e8f4fd;
  border-color: #3498db;
}

.day-number {
  font-size: 0.8rem;
  font-weight: 500;
}

.day-events {
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.event-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #3498db;
}

.event-dot.status-confirmé {
  background-color: #28a745;
}

.event-dot.status-en-attente {
  background-color: #ffc107;
}

.event-dot.status-annulé {
  background-color: #dc3545;
}

.event-dot.status-terminé {
  background-color: #6c757d;
}

.event-count {
  font-size: 0.6rem;
  color: #666;
}

.calendar-week {
  margin-bottom: 20px;
}

.week-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 5px;
}

.week-day-header {
  text-align: center;
  padding: 5px;
}

.week-day-name {
  font-size: 0.7rem;
  color: #666;
}

.week-day-number {
  font-size: 0.9rem;
  font-weight: 500;
}

.week-day-number.today {
  color: #fff;
  background-color: #3498db;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.week-events {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.week-day-events {
  min-height: 100px;
  padding: 5px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  overflow-y: auto;
  max-height: 200px;
}

.week-event {
  margin-bottom: 5px;
  padding: 5px;
  border-radius: 5px;
  background-color: #e8f4fd;
  border-left: 3px solid #3498db;
  font-size: 0.8rem;
  cursor: pointer;
}

.week-event.status-confirmé {
  background-color: #d4edda;
  border-left-color: #28a745;
}

.week-event.status-en-attente {
  background-color: #fff3cd;
  border-left-color: #ffc107;
}

.week-event.status-annulé {
  background-color: #f8d7da;
  border-left-color: #dc3545;
}

.week-event.status-terminé {
  background-color: #e2e3e5;
  border-left-color: #6c757d;
}

.event-time {
  font-weight: 500;
  margin-bottom: 2px;
}

.event-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-motif {
  font-size: 0.7rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-events {
  font-size: 0.7rem;
  color: #999;
  text-align: center;
  padding: 10px 0;
}

.calendar-day-view {
  margin-bottom: 20px;
}

.day-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.day-title {
  font-weight: 500;
  margin-left: 10px;
}

.day-events {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.day-event {
  padding: 10px;
  border-radius: 5px;
  background-color: #e8f4fd;
  border-left: 3px solid #3498db;
  cursor: pointer;
}

.day-event.status-confirmé {
  background-color: #d4edda;
  border-left-color: #28a745;
}

.day-event.status-en-attente {
  background-color: #fff3cd;
  border-left-color: #ffc107;
}

.day-event.status-annulé {
  background-color: #f8d7da;
  border-left-color: #dc3545;
}

.day-event.status-terminé {
  background-color: #e2e3e5;
  border-left-color: #6c757d;
}

.event-patient {
  font-weight: 500;
  margin-bottom: 5px;
}

.event-location {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #666;
  margin-top: 5px;
}

.rdv-list {
  margin-top: 20px;
}

.rdv-list h4 {
  margin-bottom: 10px;
  font-size: 1rem;
}

.rdv-item {
  padding: 10px;
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  margin-bottom: 10px;
  cursor: pointer;
}

.rdv-date, .rdv-time {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 5px;
}

.rdv-patient {
  font-weight: 500;
  margin-bottom: 5px;
}

.rdv-status {
  display: flex;
  justify-content: flex-end;
}

.status-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: capitalize;
}

.status-actif, .status-confirmé {
  background-color: #d4edda;
  color: #155724;
}

.status-inactif, .status-en-attente {
  background-color: #fff3cd;
  color: #856404;
}

.status-archive, .status-annulé {
  background-color: #f8d7da;
  color: #721c24;
}

.status-terminé {
  background-color: #e2e3e5;
  color: #383d41;
}

.btn-all {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  padding: 10px;
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  color: #666;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 10px;
}

/* Patients */
.patient-filters {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
}

.filter-toggle {
  display: flex;
  align-items: center;
}

.filter-toggle label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.patients-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.patient-card {
  padding: 15px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  cursor: pointer;
}

.patient-info {
  flex: 1;
}

.patient-name {
  font-weight: 500;
  margin-bottom: 5px;
}

.patient-age {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 5px;
}

.patient-phone, .patient-email {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 3px;
}

.patient-status {
  display: flex;
  align-items: flex-start;
}

.patient-history {
  margin-top: 20px;
}

.patient-history h5 {
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-item {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 5px;
  border-left: 3px solid #3498db;
}

.history-date {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 5px;
}

.history-motif {
  font-size: 0.9rem;
  margin-bottom: 5px;
}

.history-status {
  display: flex;
  justify-content: flex-end;
}

/* Messagerie */
.conversations-list {
  display: flex;
  flex-direction: column;
}

.conversation-item {
  display: flex;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
}

.conversation-item.unread {
  background-color: #e8f4fd;
}

.conversation-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #3498db;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  margin-right: 10px;
}

.conversation-info {
  flex: 1;
}

.conversation-name {
  font-weight: 500;
  margin-bottom: 3px;
}

.conversation-preview {
  font-size: 0.8rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.conversation-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.conversation-time {
  font-size: 0.7rem;
  color: #666;
  margin-bottom: 5px;
}

.conversation-badge {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #3498db;
  color: #fff;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.conversation-view {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #fff;
  z-index: 50;
  display: flex;
  flex-direction: column;
}

.conversation-header {
  display: flex;
  align-items: center;
  padding: 15px;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.conversation-title {
  font-weight: 500;
  margin-left: 10px;
}

.messages-container {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  background-color: #f8f9fa;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.message {
  max-width: 80%;
  padding: 10px;
  border-radius: 10px;
  position: relative;
}

.message.sent {
  align-self: flex-end;
  background-color: #3498db;
  color: #fff;
  border-bottom-right-radius: 0;
}

.message.received {
  align-self: flex-start;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-bottom-left-radius: 0;
}

.message-text {
  word-break: break-word;
}

.message-time {
  font-size: 0.7rem;
  opacity: 0.7;
  text-align: right;
  margin-top: 5px;
}

.message-attachment img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 5px;
  margin-top: 5px;
  cursor: pointer;
}

.file-attachment {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  margin-top: 5px;
  cursor: pointer;
}

.message-audio {
  margin-top: 5px;
}

.message-audio audio {
  width: 100%;
  height: 30px;
}

.audio-info {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  opacity: 0.7;
}

.no-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
}

.no-messages-icon {
  font-size: 3rem;
  margin-bottom: 10px;
}

.no-messages-text {
  font-size: 0.9rem;
}

.message-composer {
  padding: 10px;
  background-color: #fff;
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.message-input {
  flex: 1;
}

.message-input input {
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  font-size: 0.9rem;
}

.btn-attach, .btn-record, .btn-send {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #3498db;
  cursor: pointer;
  padding: 5px;
}

.btn-send.disabled {
  color: #ccc;
  cursor: not-allowed;
}

.recording-container {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: #f8d7da;
  border-radius: 20px;
}

.recording-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #721c24;
}

.recording-actions {
  display: flex;
  gap: 10px;
}

.btn-stop {
  background-color: #dc3545;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.btn-cancel {
  background: none;
  border: none;
  color: #721c24;
  font-size: 1.2rem;
  cursor: pointer;
}

.audio-preview {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.audio-preview audio {
  flex: 1;
  height: 30px;
}

.attachment-preview {
  padding: 5px 10px;
  background-color: #f8f9fa;
  border-radius: 5px;
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.attachment-name {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #666;
}

.btn-remove {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
}

/* Affiliations */
.affiliation-tabs {
  display: flex;
  margin-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
}

.tab-btn {
  flex: 1;
  padding: 10px;
  background: none;
  border: none;
  font-size: 0.9rem;
  color: #666;
  cursor: pointer;
}

.tab-btn.active {
  color: #3498db;
  border-bottom: 2px solid #3498db;
}

.structures-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.structure-card {
  padding: 15px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  cursor: pointer;
}

.structure-card.pending {
  background-color: #fff3cd;
}

.structure-info {
  flex: 1;
}

.structure-name {
  font-weight: 500;
  margin-bottom: 5px;
}

.structure-type {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 5px;
}

.structure-address, .structure-phone {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 3px;
}

.structure-status {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #856404;
  margin-bottom: 5px;
}

.request-date {
  font-size: 0.7rem;
  color: #666;
}

.structure-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.btn-affiliate, .btn-unaffiliate, .btn-cancel {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 5px;
}

.btn-affiliate {
  color: #28a745;
}

.btn-unaffiliate {
  color: #6c757d;
}

.btn-cancel {
  color: #dc3545;
}

/* Profil */
.profile-card {
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 15px;
  margin-bottom: 15px;
}

.profile-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}

.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #3498db;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.profile-name {
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 5px;
}

.profile-specialty {
  font-size: 0.9rem;
  color: #666;
}

.profile-section {
  margin-bottom: 20px;
}

.profile-section h4 {
  font-size: 1rem;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #e0e0e0;
}

.profile-field {
  margin-bottom: 10px;
}

.field-label {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 3px;
}

.field-value {
  font-size: 0.95rem;
}

.availability-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.availability-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.day-name {
  width: 80px;
  font-size: 0.9rem;
}

.day-toggle {
  width: 60px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 20px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #3498db;
}

input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

.day-hours {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 5px;
}

.day-hours input {
  width: 80px;
  padding: 5px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  font-size: 0.8rem;
}

.profile-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.btn-password, .btn-logout {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px;
  border-radius: 5px;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-password {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  color: #333;
}

.btn-logout {
  background-color: #dc3545;
  border: none;
  color: #fff;
}

/* Sauvegardes */
.backups-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.backup-card {
  padding: 15px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  cursor: pointer;
}

.backup-info {
  flex: 1;
}

.backup-name {
  font-weight: 500;
  margin-bottom: 5px;
}

.backup-description {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 10px;
}

.backup-meta {
  display: flex;
  gap: 15px;
}

.backup-date, .backup-count {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #666;
}

.backup-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
}

.backup-patients {
  margin-top: 20px;
}

.backup-patients h5 {
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.patients-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 200px;
  overflow-y: auto;
}

.patient-item {
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.patient-name {
  font-size: 0.9rem;
}

.patient-age {
  font-size: 0.8rem;
  color: #666;
}

.patients-select {
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  padding: 5px;
}

.patient-option {
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.patient-option label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

/* Paramètres */
.settings-card {
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 15px;
  margin-bottom: 15px;
}

.settings-section {
  margin-bottom: 20px;
}

.settings-section h4 {
  font-size: 1rem;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #e0e0e0;
}

.settings-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.settings-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
}

.settings-value {
  font-size: 0.9rem;
  color: #666;
}

.btn-change, .btn-navigate {
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 0.8rem;
  cursor: pointer;
}

.btn-change {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  color: #333;
}

.btn-navigate {
  background-color: #3498db;
  border: none;
  color: #fff;
}

/* Bouton flottant */
.fab {
  position: fixed;
  bottom: 70px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #3498db;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 5;
}

/* Message pour aucune donnée */
.no-data {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 0.9rem;
}

/* Responsive */
@media (min-width: 768px) {
  .mobile-dashboard {
    max-width: 500px;
    margin: 0 auto;
    border-left: 1px solid #e0e0e0;
    border-right: 1px solid #e0e0e0;
  }
  
  .mobile-side-menu {
    max-width: 300px;
  }
}


        `}


      </style>
    
    </div>
  );
};

export default Medecins;
